/**
 * Volumes list page component.
 *
 * @module pages/VolumesPage
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Database,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Plus,
  HardDrive,
  Search,
  Lock,
  GitFork,
} from 'lucide-react';
import { LoadingPage, ConfirmModal } from '@/components/ui';
import { ForkVolumeModal } from '@/components/ForkVolumeModal';
import {
  useVolumes,
  useDeleteVolume,
  useForkVolume,
  useSandboxGroupVolumes,
  useDeleteSandboxGroupVolume,
  useForkSandboxGroupVolume,
} from '@/hooks';
import type { VolumeResponse } from '@/types';

/**
 * Volumes list page
 */
export function VolumesPage() {
  const { groupId } = useParams<{ groupId?: string }>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Group-scoped data
  const { data: groupVolumes, isLoading: groupLoading } = useSandboxGroupVolumes(
    groupId ?? '',
    !!groupId
  );
  const deleteGroupVolumeMutation = useDeleteSandboxGroupVolume();

  // Default (non-group) data (disabled when groupId is present)
  const { data: userVolumes, isLoading: userLoading } = useVolumes(page, pageSize, !groupId);
  const deleteMutation = useDeleteVolume();
  const forkMutation = useForkVolume();
  const forkGroupMutation = useForkSandboxGroupVolume();

  const volumes = groupId ? groupVolumes : userVolumes;
  const isLoading = groupId ? groupLoading : userLoading;

  // URL prefix for links
  const basePath = groupId ? `/sandbox-groups/${encodeURIComponent(groupId)}/volumes` : '/volumes';

  const [deleteTarget, setDeleteTarget] = useState<VolumeResponse | null>(null);
  const [forkTarget, setForkTarget] = useState<VolumeResponse | null>(null);
  const [forkError, setForkError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentName: '',
  });
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Filter volumes based on search query
  const filteredVolumes = volumes?.filter((volume) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();

    // Search in volume name
    if (volume.volumeName.toLowerCase().includes(query)) {
      return true;
    }

    // Search in labels (keys and values)
    if (volume.labels) {
      const labelsMatch = Object.entries(volume.labels as Record<string, string>).some(
        ([key, value]) => key.toLowerCase().includes(query) || value.toLowerCase().includes(query)
      );
      if (labelsMatch) {
        return true;
      }
    }

    return false;
  });

  // We don't have a count endpoint for volumes so we use the array length
  const total = volumes?.length ?? 0;

  const handleDelete = () => {
    if (deleteTarget) {
      if (groupId) {
        deleteGroupVolumeMutation.mutate(
          { groupId, volumeName: deleteTarget.volumeName },
          { onSuccess: () => setDeleteTarget(null) }
        );
      } else {
        deleteMutation.mutate(deleteTarget.volumeName, {
          onSuccess: () => setDeleteTarget(null),
        });
      }
    }
  };

  const handleFork = (destinationVolumeName: string) => {
    if (!forkTarget) return;
    setForkError(null);
    const onSuccess = () => {
      setForkTarget(null);
      setForkError(null);
    };
    const onError = (error: Error) => {
      setForkError(error.message);
    };

    if (groupId) {
      forkGroupMutation.mutate(
        {
          groupId,
          sourceVolumeName: forkTarget.volumeName,
          destinationVolumeName,
          labels: { 'fork-source': forkTarget.volumeName },
        },
        { onSuccess, onError }
      );
    } else {
      forkMutation.mutate(
        {
          sourceVolumeName: forkTarget.volumeName,
          destinationVolumeName,
          labels: { 'fork-source': forkTarget.volumeName },
        },
        { onSuccess, onError }
      );
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    // If we got a full page, there may be more
    if (volumes && volumes.length === pageSize) {
      setPage(page + 1);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && volumes) {
      setSelectedNames(new Set(volumes.map((v) => v.volumeName)));
    } else {
      setSelectedNames(new Set());
    }
  };

  const handleSelectOne = (name: string, checked: boolean) => {
    const newSelected = new Set(selectedNames);
    if (checked) {
      newSelected.add(name);
    } else {
      newSelected.delete(name);
    }
    setSelectedNames(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedNames.size === 0) return;

    setIsBulkDeleting(true);
    const namesToDelete = Array.from(selectedNames);
    setBulkDeleteProgress({ current: 0, total: namesToDelete.length, currentName: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    for (let i = 0; i < namesToDelete.length; i += BATCH_SIZE) {
      const batch = namesToDelete.slice(i, i + BATCH_SIZE);

      await Promise.all(
        batch.map(async (name) => {
          try {
            if (groupId) {
              await deleteGroupVolumeMutation.mutateAsync({ groupId, volumeName: name });
            } else {
              await deleteMutation.mutateAsync(name);
            }
          } catch (error) {
            console.error(`Failed to delete volume ${name}:`, error);
          }
          completed++;
          setBulkDeleteProgress({
            current: completed,
            total: namesToDelete.length,
            currentName: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : name,
          });
        })
      );
    }

    setBulkDeleteProgress({
      current: namesToDelete.length,
      total: namesToDelete.length,
      currentName: '',
    });
    setIsBulkDeleting(false);
    setSelectedNames(new Set());

    setTimeout(() => {
      setShowBulkDeleteModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentName: '' });
    }, 1000);
  };

  const isAllSelected =
    volumes && volumes.length > 0 && volumes.every((v) => selectedNames.has(v.volumeName));

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Volumes</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Link
            to={`${basePath}/new`}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>
              <span className="hidden sm:inline">Create </span>Volume
            </span>
          </Link>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-3 mb-2">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or labels"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search volumes by name or labels"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Bulk actions */}
      {selectedNames.size > 0 && (
        <div className="mb-2 flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {selectedNames.size} volume{selectedNames.size > 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <table className="w-full" aria-label="Volumes">
            <thead className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
              <tr>
                <th className="pl-4 pr-2 py-1.5 text-left">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                    aria-label="Select all volumes"
                  />
                </th>
                <th
                  scope="col"
                  className="pl-0 pr-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Size
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  State
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Labels
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#0a0a0a]">
              {!filteredVolumes || filteredVolumes.length === 0 ? (
                <tr className="bg-white dark:bg-[#0a0a0a]">
                  <td colSpan={7} className="px-4 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <Database className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                        {searchQuery ? 'No matching volumes found.' : 'No Volumes yet.'}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        {searchQuery
                          ? 'Try adjusting your search query.'
                          : 'Volumes provide persistent storage that can be mounted into sandboxes.'}
                      </p>
                      {!searchQuery && (
                        <Link
                          to={`${basePath}/new`}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Create Volume
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredVolumes.map((volume) => (
                  <tr
                    key={volume.volumeName}
                    className="border-b border-gray-200 dark:border-[#2a2a2a] hover:bg-gray-50 dark:hover:bg-[#161616]"
                  >
                    <td className="pl-4 pr-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={selectedNames.has(volume.volumeName)}
                        onChange={(e) => handleSelectOne(volume.volumeName, e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                        aria-label={`Select volume ${volume.volumeName}`}
                      />
                    </td>
                    <td className="pl-0 pr-4 py-1.5 whitespace-nowrap">
                      <Link
                        to={`${basePath}/${encodeURIComponent(volume.volumeName)}`}
                        className="flex items-center gap-2 group"
                      >
                        <HardDrive className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-200 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                          {volume.volumeName}
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      {volume.type === 'DataDisk' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                          Data Disk
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          {volume.type}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {volume.size ?? '-'}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      {volume.isAttached ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                          <Lock className="h-3 w-3" />
                          Attached
                        </span>
                      ) : volume.type === 'DataDisk' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          Available
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex flex-wrap gap-1">
                        {volume.labels && Object.keys(volume.labels).length > 0
                          ? Object.entries(volume.labels).map(([key, val]) => (
                              <span
                                key={key}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                              >
                                {key}={String(val)}
                              </span>
                            ))
                          : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {volume.type === 'DataDisk' && (
                          <button
                            onClick={() => setForkTarget(volume)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          >
                            <GitFork className="h-4 w-4" />
                            Fork
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteTarget(volume)}
                          disabled={volume.isAttached}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                          title={
                            volume.isAttached
                              ? 'Cannot delete while attached to a sandbox'
                              : undefined
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between px-6 py-2 border-t border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]">
          <div className="flex items-center gap-4">
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Results per page"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {searchQuery && filteredVolumes && filteredVolumes.length !== total ? (
                <>
                  Showing {filteredVolumes.length} of {total} volumes (filtered)
                </>
              ) : (
                <>
                  Showing {total > 0 ? (page - 1) * pageSize + 1 : 0} to{' '}
                  {(page - 1) * pageSize + total} volumes
                </>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Page {page}</span>
            <button
              onClick={handlePreviousPage}
              disabled={page <= 1}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handleNextPage}
              disabled={!volumes || volumes.length < pageSize}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Volume"
        message={`Are you sure you want to delete volume "${deleteTarget?.volumeName}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={groupId ? deleteGroupVolumeMutation.isPending : deleteMutation.isPending}
      />

      <ForkVolumeModal
        isOpen={!!forkTarget}
        onClose={() => {
          setForkTarget(null);
          setForkError(null);
        }}
        onFork={handleFork}
        sourceVolumeName={forkTarget?.volumeName ?? ''}
        isLoading={groupId ? forkGroupMutation.isPending : forkMutation.isPending}
        error={forkError}
      />

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-delete-vol-title"
          >
            <div className="p-6">
              <h2
                id="bulk-delete-vol-title"
                className="text-xl font-semibold text-gray-900 dark:text-white mb-4"
              >
                Delete {selectedNames.size} Volume{selectedNames.size > 1 ? 's' : ''}?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete {selectedNames.size} volume
                    {selectedNames.size > 1 ? 's' : ''}? This action cannot be undone.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete {selectedNames.size} Volume{selectedNames.size > 1 ? 's' : ''}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting volumes...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div
                      className="w-full bg-gray-200 dark:bg-[#2a2a2a] rounded-full h-2"
                      role="progressbar"
                      aria-valuenow={bulkDeleteProgress.current}
                      aria-valuemin={0}
                      aria-valuemax={bulkDeleteProgress.total}
                      aria-label="Bulk delete progress"
                    >
                      <div
                        className="bg-red-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      />
                    </div>
                    {bulkDeleteProgress.currentName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 truncate">
                        Processing: {bulkDeleteProgress.currentName}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      All volumes deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
